import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import EmployeeAnalytics from '../../components/employees/EmployeeAnalytics';
import EmployeeDetailsModal from '../../components/employees/EmployeeDetailsModal';
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { showError, showSuccess } from '../../utils/toast';

const EmployeesManagementPage = () => {
  const {t} = useTranslation();
  const { apiCall } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // list, analytics

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    permissions: [],
    hireDate: '',
    salary: ''
  });

  const departments = [
    { value: 'management', label: t("company.employees.departments.management") },
    { value: 'medical', label: t("company.employees.departments.medical") },
    { value: 'grooming', label: t("company.employees.departments.grooming") },
    { value: 'reception', label: t("company.employees.departments.reception") },
    { value: 'inventory', label: t("company.employees.departments.inventory") },
    { value: 'analytics', label: t("company.employees.departments.analytics") }
  ];

  const predefinedRoles = [
    { value: 'manager', label: t("company.employees.roles.manager"), department: 'management' },
    { value: 'veterinarian', label: t("company.employees.roles.veterinarian"), department: 'medical' },
    { value: 'groomer', label: t("company.employees.roles.groomer"), department: 'grooming' },
    { value: 'receptionist', label: t("company.employees.roles.receptionist"), department: 'reception' },
    { value: 'cashier', label: t("company.employees.roles.cashier"), department: 'reception' },
    { value: 'analyst', label: t("company.employees.roles.analyst"), department: 'analytics' }
  ];

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleOpenModal = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setSelectedEmployee(null);
    setShowDetailsModal(false);
  };

  useEffect(() => {
    loadEmployees();
    loadRolesAndPermissions();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, roleFilter, departmentFilter, statusFilter]);

  const loadEmployees = async () => {
    try {
      setIsLoading(true);
      const response = await apiCall('/companies/employees', 'GET');
      if (response.success) {
        setEmployees(response.employees || []);
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
      // Show mock data for demonstration
      setEmployees([
        {
          id: '1',
          first_name: 'John',
          last_name: 'Doe',
          username: 'john.doe',
          email: 'john@example.com',
          phone: '+1234567890',
          role: 'veterinarian',
          department: 'medical',
          permissions: ['view_bookings', 'edit_bookings', 'view_customers'],
          hire_date: '2023-01-15',
          salary: 75000,
          is_active: true,
          last_login: '2024-01-15T10:30:00Z'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRolesAndPermissions = async () => {
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        apiCall('/companies/employees/reference/roles', 'GET'),
        apiCall('/companies/employees/reference/permissions', 'GET')
      ]);
      
      if (rolesResponse.success) {
        setAvailableRoles(rolesResponse.roles || []);
      }
      if (permissionsResponse.success) {
        setAvailablePermissions(permissionsResponse.permissions || []);
      }
    } catch (error) {
      console.error('Failed to load roles and permissions:', error);
    }
  };

  const filterEmployees = () => {
    let filtered = employees;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        employee.first_name?.toLowerCase().includes(term) ||
        employee.last_name?.toLowerCase().includes(term) ||
        employee.email?.toLowerCase().includes(term) ||
        employee.username?.toLowerCase().includes(term) ||
        employee.phone?.includes(term)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(employee => employee.role === roleFilter);
    }

    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(employee => employee.department === departmentFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(employee => employee.is_active === isActive);
    }

    setFilteredEmployees(filtered);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: '',
      department: '',
      permissions: [],
      hireDate: '',
      salary: ''
    });
  };

  const handleCreateEmployee = () => {
    resetForm();
    setEditingEmployee(null);
    setShowForm(true);
  };

  const handleEditEmployee = (employee) => {
    // Извлекаем YYYY-MM-DD для input type="date"
    const formattedHireDate = employee.hire_date 
      ? employee.hire_date.substring(0, 10) 
      : '';

    setFormData({
      username: employee.username || '',
      password: '',
      firstName: employee.first_name || '',
      lastName: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      role: employee.role || '',
      department: employee.department || '',
      permissions: employee.permissions || [],
      hireDate: formattedHireDate,
      salary: employee.salary !== null && employee.salary !== undefined
        ? String(employee.salary)
        : ''
    });

    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        role: formData.role,
        department: formData.department,
        permissions: formData.permissions,
        hire_date: formData.hireDate || null,
        salary: parseFloat(formData.salary) || 0.0,
        is_active: formData.isActive !== undefined ? formData.isActive : true // Default to true for new
      };
      
      // Add password only for new employees
      if (!editingEmployee) {
        payload.password = formData.password;
      }

      const endpoint = editingEmployee 
        ? `/companies/employees/${editingEmployee.id}`
        : '/companies/employees';
      const method = editingEmployee ? 'PUT' : 'POST';

      const response = await apiCall(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.success) {
        showSuccess(editingEmployee ? t("company.employees.alerts.updat_success") : t("company.employees.alerts.save_success"));
        setShowForm(false);
        resetForm();
        loadEmployees();
      } else {
        showError(response.error || t("company.employees.alerts.save_error"));
      }
    } catch (error) {
      console.error('❌ FULL ERROR:', error);
      showError(error.message || t("company.employees.alerts.save_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivateEmployee = async (employee) => {
    if (!confirm(t("company.employees.alerts.confirm_delete") + ` ${employee.first_name} ${employee.last_name}?`)) {
      return;
    }

    try {
      const response = await apiCall(`/companies/employees/${employee.id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSuccess(t("company.employees.alerts.delete_success"));
        setEmployees(prev => prev.filter(e => e.id !== employee.id));
      } else {
        showError(response.error || t("company.employees.alerts.error_delete"));
      }
    } catch (error) {
      console.error('❌ DELETE ERROR:', error);
      showError(t("company.employees.alerts.error_delete"));
    }
  };

  const handleManagePermissions = (employee) => {
    setSelectedEmployee(employee);
    setShowPermissionsModal(true);
  };

  const handleViewSchedule = (employee) => {
    setSelectedEmployee(employee);
    setShowScheduleModal(true);
  };

  const handleRoleChange = (role) => {
    setFormData(prev => {
      const selectedRole = predefinedRoles.find(r => r.value === role);
      return {
        ...prev,
        role,
        department: selectedRole ? selectedRole.department : prev.department
      };
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("company.employees.lodaing")}</p>
        </div>
      </div>
    );
  }

  // Show analytics view
  if (currentView === 'analytics') {
    return <EmployeeAnalytics />;
  }

  return (
    <div className="min-h-screen text-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-10">
        
        {/* Filters Panel */}
        <div className="bg-white border border-zinc-200/60 rounded-[32px] p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-1">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                placeholder={t("company.employees.filters.search_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-zinc-50 border-transparent focus:bg-white focus:ring-2 focus:ring-orange-500/20 rounded-2xl text-sm font-medium transition-all"
              />
            </div>

            <div className="relative">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full bg-zinc-50 border-transparent rounded-2xl text-sm font-medium py-3 px-4 focus:ring-2 focus:ring-orange-500/20 appearance-none cursor-pointer"
              >
                <option value="all">{t("company.employees.filters.all_roles")}</option>
                {predefinedRoles.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
            </div>

            <div className="relative">
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full bg-zinc-50 border-transparent rounded-2xl text-sm font-medium py-3 px-4 focus:ring-2 focus:ring-orange-500/20 appearance-none cursor-pointer"
              >
                <option value="all">{t("company.employees.filters.all_departments")}</option>
                {departments.map(dept => <option key={dept.value} value={dept.value}>{dept.label}</option>)}
              </select>
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-zinc-50 border-transparent rounded-2xl text-sm font-medium py-3 px-4 focus:ring-2 focus:ring-orange-500/20 appearance-none cursor-pointer"
              >
                <option value="all">{t("company.employees.filters.any_status")}</option>
                <option value="active">{t("company.employees.filters.status_active")}</option>
                <option value="inactive">{t("company.employees.filters.status_inactive")}</option>
              </select>
            </div>
            <button
                onClick={handleCreateEmployee}
                className="flex items-center justify-center px-4 py-3 text-sm font-bold text-white bg-orange-600 hover:bg-orange-700 transition-all rounded-2xl shadow-sm"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                {t("company.employees.filters.btn_add")}
            </button>
            <button
              onClick={() => { setSearchTerm(''); setRoleFilter('all'); setDepartmentFilter('all'); setStatusFilter('all'); }}
              className="flex items-center justify-center px-4 py-3 text-sm font-bold text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-all rounded-2xl"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              {t("company.employees.filters.btn_reset")}
            </button>
          </div>
        </div>

        {/* Employees List */}
        <div className="bg-white border border-zinc-200/60 rounded-[40px] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-8 py-6 text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">{t("company.employees.table.cols.employee")}</th>
                  <th className="px-6 py-6 text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">{t("company.employees.table.cols.position")}</th>
                  <th className="px-6 py-6 text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">{t("company.employees.table.cols.contact")}</th>
                  <th className="px-6 py-6 text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">{t("company.employees.table.cols.status")}</th>
                  <th className="px-8 py-6 text-right text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">{t("company.employees.table.cols.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="group hover:bg-zinc-50/50 transition-all">
                    <td className="px-8 py-5">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-[18px] bg-zinc-100 border border-zinc-200 flex items-center justify-center overflow-hidden">
                          <UserCircleIcon className="h-10 w-10 text-zinc-300" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-zinc-900">{employee.first_name} {employee.last_name}</div>
                          <div className="text-xs font-medium text-zinc-400">@{employee.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col gap-1.5">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg bg-zinc-900 text-[10px] font-bold text-white uppercase w-fit tracking-wide">
                          {employee.role}
                        </span>
                        <span className="text-xs font-bold text-zinc-400 flex items-center capitalize">
                          <BuildingOfficeIcon className="h-3 w-3 mr-1.5" />
                          {employee.department}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm font-medium text-zinc-600">
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-2 text-zinc-300" />
                          {employee.email}
                        </div>
                        {employee.phone && (
                          <div className="flex items-center text-xs text-zinc-400">
                            <PhoneIcon className="h-3.5 w-3.5 mr-2 text-zinc-300" />
                            {employee.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className={`inline-flex items-center px-3 py-1 rounded-xl text-[11px] font-bold tracking-wide uppercase ${
                        employee.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-zinc-100 text-zinc-500'
                      }`}>
                        <div className={`h-1.5 w-1.5 rounded-full mr-2 ${employee.is_active ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                        {employee.is_active ? t("company.employees.status.active") : t("company.employees.status.offline")}
                      </div>
                    </td>
                    <td className="px-8 py-5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5 opacity-100 transition-opacity">
                        <button
                          onClick={() => handleOpenModal(employee)}
                          className="p-2 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                          title={t("company.employees.tooltips.details")}
                        >
                          <ShieldCheckIcon className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleViewSchedule(employee)} className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all" title={t("company.employees.tooltips.schedule")}>
                          <CalendarDaysIcon className="h-5 w-5" />
                        </button>
                        {employee.is_active && (
                          <button onClick={() => handleDeactivateEmployee(employee)} className="p-2 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all" title={t("company.employees.tooltips.remove")}>
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Main Modal Overlay for Form */}
      {showForm && (
        <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-[40px] w-full max-w-2xl shadow-2xl border border-zinc-100 overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-10">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl font-black text-zinc-900 tracking-tight">
                  {editingEmployee ? t("company.employees.tooltips.title_edit") : t("company.employees.modal.title_create")}
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400"
                >
                  <XCircleIcon className="h-8 w-8" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

                  {/* Username */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.username")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                    />
                  </div>

                  {/* Password */}
                  {!editingEmployee && (
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                        {t("company.employees.form.password")} *
                      </label>
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                      />
                    </div>
                  )}

                  {/* First Name */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.first_name")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                    />
                  </div>

                  {/* Last Name */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.last_name")} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.email")} *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.phone")}
                    </label>
                    <div className="relative">
                      <PhoneIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="tel"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full pl-11 pr-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                        placeholder="+993..."
                      />
                    </div>
                  </div>

                  {/* Hire Date */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.hire_date")}
                    </label>
                    <div className="relative">
                      <CalendarDaysIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="date"
                        value={formData.hireDate || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, hireDate: e.target.value }))}
                        className="w-full pl-11 pr-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium text-zinc-700"
                      />
                    </div>
                  </div>

                  {/* Salary */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.salary")} (USD)
                    </label>
                    <div className="relative">
                      <CurrencyDollarIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="number"
                        step="0.01"
                        value={formData.salary ?? ''} 
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            salary: val === '' ? '' : parseFloat(val)
                          }));
                        }}
                        className="w-full pl-11 pr-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest ml-1">
                      {t("company.employees.form.role")} *
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-[20px] focus:ring-2 focus:ring-orange-500/20 font-medium appearance-none"
                    >
                      <option value="">{t("company.employees.form.select_placeholder")}</option>
                      {predefinedRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                </div>

                {/* Buttons */}
                <div className="flex justify-end items-center gap-4 pt-4 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-8 py-4 text-sm font-bold text-zinc-400 hover:text-zinc-900 transition-colors"
                  >
                    {t("company.employees.modal.btn_cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-10 py-4 bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-bold rounded-[22px] shadow-lg disabled:opacity-50 transition-all"
                  >
                    {isSubmitting ? t("company.employees.modal.btn_submitting") : t("company.employees.modal.btn_confirm")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <EmployeeDetailsModal 
          employee={selectedEmployee}
          onClose={handleCloseModal} 
          onEdit={() => {
            handleCloseModal();
            handleEditEmployee(selectedEmployee);
          }}
        />
      )}
    </div>
  );
};

export default EmployeesManagementPage;